import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api, { describeSaveError } from '../../utils/authUtils';
import { useLanguage } from '../../contexts/LanguageContext';
import { getCountryOptions } from '../../utils/countries';
import { MIN_INTERESTS } from '../../constants/doctorProfile';
import ProfileFormFields from '../attendee/ProfileFormFields';

const BLUE = '#47ABDB';

// Same Hero/Grey layout as AttendeeProfile.js / SpeakerDashboard.js, so this
// reads as part of the same product rather than a bolted-on extra step.
const Page = styled.div`margin:1.5rem 1rem 3rem;position:relative;@media(max-width:640px){margin:0 0 2rem;}`;
const Hero = styled.div`position:relative;background:#fff;box-shadow:0 25px 50px -12px rgba(0,0,0,.18);`;
const HeroInner = styled.div`max-width:78rem;margin:0 auto;padding:3.75rem 2.5rem 3.25rem;@media(max-width:600px){padding:2.5rem 1.5rem;}`;
const BigHeadline = styled.h1`margin:0;color:${BLUE};font-weight:800;text-transform:uppercase;letter-spacing:.02em;line-height:1.04;font-size:clamp(2.2rem,5.2vw,3.8rem);`;
const SubHeadline = styled.p`margin:.85rem 0 0;color:#5B5B5B;font-weight:700;font-size:clamp(1rem,1.9vw,1.35rem);`;
const TagLine = styled.p`margin:.5rem 0 0;color:#5B5B5B;font-weight:400;font-style:italic;font-size:clamp(.85rem,1.3vw,1rem);`;
const Grey = styled.div`background:#d0e6ef;`;
const GreyInner = styled.div`max-width:52rem;margin:0 auto;padding:2rem 2.5rem;@media(max-width:640px){padding:1.5rem;}`;
const SuccessBanner = styled.div`background-color:#d1fae5;color:#065f46;padding:1rem;margin-bottom:1.5rem;text-align:center;`;
const Card = styled.div`background:#fff;padding:1.75rem;box-shadow:0 8px 24px rgba(15,23,42,.08);`;
const CardTitle = styled.h2`margin:0 0 .5rem;font-size:1.2rem;color:#111D4E;`;
const Intro = styled.p`color:#334155;margin:0 0 1.5rem;font-size:.95rem;line-height:1.5;`;
const Empty = styled.p`color:#5B6B78;`;

// Landed on right after accepting a speaker invitation (see
// SpeakerInvitationLanding.js). Whatever an admin edited, sent with an
// invite, or resolved from AI research (see SpeakerProfileService.
// stagingUserFor) is proposed here rather than blocking on a separate
// decision screen first - only a field that was genuinely blank gets
// filled in automatically (see the pending-review effect below); a field
// that already has something real keeps it, since the account holder's
// own current data is always the authoritative one (last write wins - see
// docs/speaker-profile-staging-design.md). Anything proposed on top of
// real data still shows up as an opt-in via `fieldDiffs` (passed to
// ProfileFormFields, which renders the per-field "newer value available"
// hint) - a single click there is the whole review, instead of a separate
// current-vs-proposed gate before the speaker can even reach their own
// profile. For a new account, this is also the first time they've seen
// their AI-researched, admin-reviewed profile themselves; for a returning
// speaker confirming a second event, it's usually just a receipt.
const SpeakerInviteConfirmation = () => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const location = useLocation();
  const navigate = useNavigate();
  const eventName = location.state?.eventName;
  const { language } = useLanguage();
  const countryOptions = useMemo(() => getCountryOptions(language), [language]);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  // 'checking' while the pending-review resolve is in flight; 'profile'
  // once whatever was staged has been applied and it's safe to load the
  // (now up to date) live profile below.
  const [step, setStep] = useState('checking');
  const [fieldDiffs, setFieldDiffs] = useState([]);
  // React.StrictMode (index.js) deliberately double-invokes effects in dev
  // to surface exactly this class of bug: this effect fires a real,
  // non-idempotent POST with no guard, so without this ref it genuinely
  // raced itself - two near-simultaneous resolve calls against the same
  // profile, each reading the pre-resolve state, one clobbering what the
  // other had just written (confirmed live: paired resolve calls landing at
  // the same millisecond, and a live "Row was updated by another
  // transaction" conflict from the same window). The ref survives the
  // mount->cleanup->remount cycle StrictMode does within one instance, so
  // the second invocation is a no-op; a genuinely new visit still resolves.
  const resolvedOnce = useRef(false);

  useEffect(() => {
    if (resolvedOnce.current) return;
    resolvedOnce.current = true;
    let diffs = [];
    api.get('/api/speaker/profile/pending-review')
      .then(({ data }) => {
        diffs = data.diffs || [];
        // Only a field that was genuinely blank gets auto-filled - a field
        // that already has something real is left as-is (the account
        // holder's own current data is the authoritative one, last write
        // wins); the form below still shows the proposed value as an
        // opt-in via ProfileFormFields' revert/apply hint, it's just never
        // silently applied on their behalf. Same default rule as everywhere
        // else in this system (see FieldDiffPicker.defaultResolutions).
        const resolutions = Object.fromEntries(diffs.map((d) => [d.field, d.currentValue ? 'CURRENT' : 'PROPOSED']));
        return api.post('/api/speaker/profile/pending-review/resolve', resolutions);
      })
      // Nothing was pending, or a transient failure either way - nothing
      // left worth blocking them on here.
      .catch(() => {})
      .finally(() => { setFieldDiffs(diffs); setStep('profile'); });
  }, []);

  useEffect(() => {
    if (step !== 'profile') return;
    api.get('/api/attendee/profile')
      .then(({ data }) => { setProfile(data); setDraft(data); })
      .catch(() => setProfile({ error: true }));
  }, [step]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDraft({ ...draft, [name]: value });
  };
  const toggleDraftList = (key, value) => {
    setDraft((current) => ({
      ...current,
      [key]: (current[key] || []).includes(value)
        ? current[key].filter((v) => v !== value)
        : [...(current[key] || []), value],
    }));
  };
  const roleLabel = (status) => t(`speaker.role.${status}`, status.replaceAll('_', ' '));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    if ((draft.interests || []).length < MIN_INTERESTS) {
      setMessage({ text: t('speaker.onboarding.err.interests', 'Please select at least three cardiac interests.'), error: true });
      return;
    }
    setSaving(true);
    try {
      await api.put('/api/attendee/profile', draft);
      navigate('/profile');
    } catch (err) {
      console.error('Profile save failed:', err);
      setMessage({ text: describeSaveError(err, t), error: true });
    } finally {
      setSaving(false);
    }
  };

  if (step === 'checking' || !profile) {
    return <Page><Hero><HeroInner><Empty>{t('profile.loading', 'Loading your profile…')}</Empty></HeroInner></Hero></Page>;
  }
  if (profile.error) {
    return <Page><Hero><HeroInner><Empty>{t('profile.loadError', 'Your profile could not be loaded.')}</Empty></HeroInner></Hero></Page>;
  }

  return (
    <Page>
      <Hero>
        <HeroInner>
          <BigHeadline>YOUR MEDICAL CARE PILOT</BigHeadline>
          <SubHeadline>Build your Best Care with Heartport</SubHeadline>
          <TagLine>Here to support your medical superpowers with research insights and AI-enhanced clinical care tools.</TagLine>
        </HeroInner>
      </Hero>

      <Grey>
        <GreyInner>
          {eventName && (
            <SuccessBanner>
              {t('profile.invitationAccepted', "You've successfully accepted your invitation to speak at {eventName}.", { eventName })}
            </SuccessBanner>
          )}
          <Card>
            <CardTitle>{t('speaker.onboarding.title', 'Review your profile')}</CardTitle>
            <Intro>{t('speaker.onboarding.intro', "Some of this was found through AI research and reviewed by the organizers - please check it over and complete anything that's missing before it's shown publicly.")}</Intro>
            {fieldDiffs.length > 0 && (
              <Intro>{t('speaker.onboarding.diffIntro', 'Fields marked below have a newer value available - check each one and pick whichever you\'d like to keep.')}</Intro>
            )}
            <ProfileFormFields
              draft={draft} countryOptions={countryOptions} message={message} saving={saving}
              t={t} roleLabel={roleLabel}
              onChange={handleChange} onToggleList={toggleDraftList}
              onInterestsChange={(v) => setDraft((current) => ({ ...current, interests: v }))}
              onSubmit={handleSubmit}
              onCancel={() => navigate('/profile')}
              cancelLabel={t('speaker.onboarding.skip', 'Continue without changes')}
              submitLabel={t('speaker.onboarding.continue', 'Save & continue')}
              fieldDiffs={fieldDiffs}
            />
          </Card>
        </GreyInner>
      </Grey>
    </Page>
  );
};

export default SpeakerInviteConfirmation;
